import { useEffect, useRef, useState } from 'react';
import {
  enablePushNotifications,
  getCurrentSubscription,
  isPushPromptSnoozed,
  isPushSupported,
  pushAvailabilityReason,
  pushSnoozeMsRemaining,
  snoozePushPrompt,
  PUSH_SNOOZE_MS,
} from '../../services/pushNotifications';
import { useAuth } from '../../context/AuthContext';
import { useTranslation } from 'react-i18next';
import './PushPermissionPrompt.css';

// Delay corto desde el login hasta que el banner aparece la primera vez,
// para no interrumpir el primer paint.
const INITIAL_DELAY_MS = 4000;

// Banner insistente para activar notificaciones tras login.
//
// Se muestra cuando:
//   - El usuario está autenticado.
//   - El navegador soporta push (sin esto no aparece nada — la opción en
//     Ajustes muestra el motivo).
//   - No hay subscripción activa todavía.
//   - El permiso no está en "denied" (eso ya no se puede revocar desde JS).
//
// Si el usuario lo silencia con "Más tarde", desaparece, y vuelve solo
// pasadas 2 horas sin necesidad de recargar la app. Mismo patrón que el
// banner de "Instalar app".
export function PushPermissionPrompt() {
  const { t } = useTranslation();
  const { isAuthenticated } = useAuth();
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Programa la próxima aparición tras el delay indicado. Cancela cualquier
  // timer pendiente para no apilarlos.
  const scheduleShow = (delayMs: number) => {
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(async () => {
      timerRef.current = null;
      if (!isPushSupported()) return;
      if (Notification.permission === 'denied') return;
      // Si ya hay subscripción activa la sincronizamos en silencio y no
      // mostramos el banner.
      const existing = await getCurrentSubscription();
      if (existing) {
        enablePushNotifications().catch(() => {});
        return;
      }
      setShow(true);
    }, Math.max(0, delayMs));
  };

  useEffect(() => {
    if (!isAuthenticated) {
      setShow(false);
      if (timerRef.current) clearTimeout(timerRef.current);
      return;
    }
    if (!isPushSupported()) return;
    if (Notification.permission === 'denied') return;

    // Si recargamos en mitad de un snooze, esperamos lo que reste; si no,
    // arrancamos con el delay normal post-login.
    const remaining = isPushPromptSnoozed() ? pushSnoozeMsRemaining() : INITIAL_DELAY_MS;
    scheduleShow(remaining);

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
    // Solo re-evaluamos cuando cambia el estado de auth.
  }, [isAuthenticated]);

  if (!show) return null;

  const avail = pushAvailabilityReason();

  const handleActivate = async () => {
    setBusy(true);
    setErrorMsg(null);
    const res = await enablePushNotifications();
    setBusy(false);
    if (res.ok) {
      setShow(false);
    } else {
      setErrorMsg(res.reason ?? t('components.push.cannotEnable'));
    }
  };

  const handleSnooze = () => {
    snoozePushPrompt();
    setShow(false);
    // Reaparece solo cuando expire el snooze (2 h), sin recarga necesaria.
    scheduleShow(PUSH_SNOOZE_MS);
  };

  return (
    <div className="push-prompt" role="dialog" aria-label={t('components.push.ariaLabel')}>
      <div className="push-prompt__icon" aria-hidden="true">
        <svg viewBox="0 0 24 24" fill="currentColor" width="22" height="22">
          <path d="M12 22c1.1 0 2-.9 2-2h-4c0 1.1.9 2 2 2zm6-6v-5c0-3.07-1.64-5.64-4.5-6.32V4c0-.83-.67-1.5-1.5-1.5s-1.5.67-1.5 1.5v.68C7.63 5.36 6 7.92 6 11v5l-2 2v1h16v-1l-2-2z"/>
        </svg>
      </div>
      <div className="push-prompt__body">
        <div className="push-prompt__title">
          {avail.ok ? t('components.push.enableTitle') : t('components.push.unavailableTitle')}
        </div>
        <div className="push-prompt__desc">
          {avail.ok
            ? t('components.push.enableDesc')
            : avail.reason}
        </div>
        {errorMsg && <div className="push-prompt__error">{errorMsg}</div>}
      </div>
      <div className="push-prompt__actions">
        {avail.ok && (
          <button
            type="button"
            className="push-prompt__btn push-prompt__btn--primary"
            onClick={handleActivate}
            disabled={busy}
          >
            {busy ? t('components.push.enabling') : t('components.push.enable')}
          </button>
        )}
        <button type="button" className="push-prompt__btn" onClick={handleSnooze}>
          {t('components.push.later')}
        </button>
      </div>
    </div>
  );
}
